import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Calendar, User, Building2, Clock, Phone, 
  ChevronLeft, MapPin, CheckCircle2, XCircle,
  RefreshCw, AlertCircle
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

// Status badge colors
const getStatusBadge = (status) => {
  const statusMap = {
    'SCHEDULED': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Scheduled' },
    'COMPLETED': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completed' },
    'CANCELLED': { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' },
    'NO_SHOW': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'No Show' }
  };
  const config = statusMap[status?.toUpperCase()] || statusMap['SCHEDULED'];
  return (
    <Badge className={`${config.bg} ${config.text} font-medium`}>
      {config.label}
    </Badge>
  );
};

// Visit type badge
const getTypeBadge = (eventType) => {
  const isFirstVisit = eventType?.toLowerCase()?.includes('first');
  return (
    <Badge variant="outline" className={isFirstVisit ? 'border-purple-300 text-purple-700' : 'border-cyan-300 text-cyan-700'}>
      {isFirstVisit ? 'First Visit' : 'Revisit'}
    </Badge>
  );
};

// Single visit row component
const VisitRow = ({ visit }) => {
  return (
    <div className="p-4 border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Customer & Project Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-slate-900 truncate">{visit.customer_name}</h4>
            {getTypeBadge(visit.event_type)}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <Building2 size={14} className="text-slate-400" />
              {visit.project_name}
            </span>
            {visit.customer_phone && (
              <span className="flex items-center gap-1">
                <Phone size={14} className="text-slate-400" />
                {visit.customer_phone}
              </span>
            )}
          </div>
        </div>
        
        {/* Rep & Manager */}
        <div className="flex-shrink-0 text-sm">
          <div className="flex items-center gap-1 text-slate-700">
            <User size={14} className="text-slate-400" />
            <span className="font-medium">{visit.rep_name}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Manager: {visit.manager_name}
          </div>
        </div>
        
        {/* Time & Status */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="flex items-center gap-1 text-sm text-slate-700">
              <Clock size={14} className="text-slate-400" />
              {visit.visit_time || 'TBD'}
            </div>
            <div className="text-xs text-slate-500">
              {visit.visit_date}
            </div>
          </div>
          {getStatusBadge(visit.status)}
        </div>
      </div>
      
      {/* Outcome & Next Followup */}
      {(visit.outcome || visit.next_followup) && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-4 text-sm">
          {visit.outcome && (
            <div className="flex items-start gap-2">
              <CheckCircle2 size={14} className="text-emerald-500 mt-0.5" />
              <span className="text-slate-600">
                <span className="font-medium text-slate-700">Outcome:</span> {visit.outcome}
              </span>
            </div>
          )}
          {visit.next_followup && (
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-blue-500" />
              <span className="text-slate-600">
                <span className="font-medium text-slate-700">Next:</span> {visit.next_followup}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main VisitsList Component
const VisitsList = ({ 
  title = "Visits",
  filters = {},
  city = "",
  onBack,
  showHeader = true
}) => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Build header title based on filters
  const getHeaderTitle = () => {
    const parts = [];
    
    if (filters.status === 'COMPLETED') {
      parts.push('Visits Completed');
    } else if (filters.status === 'SCHEDULED') {
      parts.push('Visits Scheduled');
    } else if (filters.visit_type === 'revisit') {
      parts.push('Revisits');
    } else {
      parts.push(title);
    }
    
    if (filters.date === 'today' || filters.date === new Date().toISOString().split('T')[0]) {
      parts.push('Today');
    }
    
    if (city) {
      parts.push(`– ${city}`);
    }
    
    return parts.join(' ');
  };
  
  const fetchVisits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Convert 'today' to actual date
      const params = { ...filters };
      if (params.date === 'today') {
        params.date = new Date().toISOString().split('T')[0];
      }
      
      const response = await api.getVisitsList(params);
      setVisits(response.data.visits || []);
    } catch (err) {
      console.error('Failed to fetch visits:', err);
      setError('Failed to load visits');
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);
  
  if (loading) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          <span className="ml-3 text-slate-600">Loading visits...</span>
        </div>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center text-center">
          <AlertCircle size={48} className="text-red-400 mb-3" />
          <p className="text-slate-600">{error}</p>
          <Button variant="outline" className="mt-4" onClick={fetchVisits}>
            <RefreshCw size={16} className="mr-2" />
            Retry
          </Button>
        </div>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack} className="p-2">
                <ChevronLeft size={20} />
              </Button>
            )}
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{getHeaderTitle()}</h2>
              <p className="text-sm text-slate-500">{visits.length} visits found</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchVisits}>
            <RefreshCw size={14} className="mr-1" />
            Refresh
          </Button>
        </div>
      )}
      
      {/* Visits List */}
      <Card className="overflow-hidden">
        {/* Table Header */}
        <div className="hidden sm:grid grid-cols-12 gap-4 p-4 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
          <div className="col-span-4">Customer / Project</div>
          <div className="col-span-3">Rep / Manager</div>
          <div className="col-span-2">Time</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-1">Status</div>
        </div>
        
        {/* Visits */}
        {visits.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No visits found for the selected filters</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {visits.map((visit) => (
              <VisitRow key={visit.event_id} visit={visit} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default VisitsList;
